<template>
  <v-dialog fullscreen :transition="false" :value="true">
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn
          icon
          dark
          :to="{ name: 'customer-list', query: $route.query }"
          exact
        >
          <v-icon>arrow_back_ios</v-icon>
        </v-btn>
        <v-toolbar-title>{{
          $t('customer.detail.text.title')
        }}</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn text :to="`/customer/${id}/edit`">
            {{ $t('customer.detail.text.edit') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <template v-if="customer">
        <v-subheader>{{ $t('customer.detail.text.basicInfo') }}</v-subheader>
        <v-card-text>
          <v-row>
            <template v-for="(item, idx) in basicData">
              <v-col
                :key="idx + '-text'"
                cols="6"
                md="3"
                lg="2"
                class="grey lighten-3 text-end"
              >
                {{ item.text }}
              </v-col>
              <v-col :key="idx + '-value'" cols="6" md="3" lg="2">
                {{ item.value }}
              </v-col>
            </template>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-subheader>{{
          $t('customer.detail.text.questionnaire')
        }}</v-subheader>
        <v-card-text>
          <v-row>
            <template v-for="(item, idx) in counselingAnswers">
              <v-col
                :key="idx + '-text'"
                cols="6"
                md="3"
                class="grey lighten-3 text-end"
              >
                {{ item.text }}
              </v-col>
              <v-col :key="idx + '-value'" cols="6" md="3">
                {{ item.value }}
              </v-col>
            </template>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-subheader>{{ $t('customer.detail.text.appUser') }}</v-subheader>
        <v-card-text>
          <v-layout v-if="userdata" wrap>
            <v-flex v-for="item in userItems" :key="item.prop">
              {{ item.text }}
              <v-spacer />
              {{ userdata[item.prop] }}
            </v-flex>
          </v-layout>
          <span v-else>{{ $t('customer.detail.text.notRegistered') }}</span>
        </v-card-text>

        <v-divider />
        <v-subheader>{{
          $t('customer.detail.text.operationHistory')
        }}</v-subheader>
        <v-card-text>
          <div ref="tableContainer" />
          <OperationHistoryTable
            :customer="customer.uuid"
            :height="tableHeight"
          />
        </v-card-text>
        <div ref="bottom" />
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import OperationHistoryTable from '@/components/Reservation/OperationHistoryTable'

export default {
  name: 'CustomerDetailView',
  components: {
    OperationHistoryTable,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    customer: null,
    fields: [],
    questions: [],
    tableHeight: '300px',
  }),
  computed: {
    userdata() {
      if (!this.customer) return null
      return this.customer.userdata
    },
    userItems() {
      return [
        {
          text: this.$t('customer.detail.text.uid'),
          prop: 'uid',
        },
        {
          text: this.$t('customer.detail.text.email'),
          prop: 'username',
        },
        {
          text: this.$t('customer.detail.text.tel'),
          prop: 'phoneNumber',
        },
        {
          text: this.$t('customer.detail.text.familyName'),
          prop: 'familyName',
        },
        {
          text: this.$t('customer.detail.text.givenName'),
          prop: 'firstName',
        },
        ...(this.$i18n.locale === 'ja'
          ? [
              {
                text: this.$t('customer.detail.text.familyNameKana'),
                prop: 'familyNameKana',
              },
              {
                text: this.$t('customer.detail.text.givenNameKana'),
                prop: 'firstNameKana',
              },
            ]
          : []),
      ]
    },
    basicData() {
      let c = this.customer
      if (!c) return []
      return [
        {
          text: this.$t('customer.detail.text.memberNumber'),
          value: c.account,
        },
        {
          text: this.$t('customer.detail.text.registeredShop'),
          value: c.shopName,
        },
        {
          text: this.$t('customer.detail.text.name'),
          value: `${c.familyName} ${c.givenName}`,
        },
        ...(this.$i18n.locale === 'ja'
          ? [
              {
                text: this.$t('customer.detail.text.furigana'),
                value: `${c.familyNameKana} ${c.givenNameKana}`,
              },
            ]
          : []),
        { text: this.$t('customer.detail.text.age'), value: c.age },
        { text: this.$t('customer.detail.text.gender'), value: c.gender },
        { text: this.$t('customer.detail.text.birthday'), value: c.birthday },

        {
          text: this.$t('customer.detail.text.address'),
          value: `${c.postcode} ${c.prefecture} ${c.city} ${c.address} ${c.building}`,
        },
        {
          text: this.$t('customer.detail.text.phoneNumber'),
          value: c.phoneNumber,
        },
        { text: this.$t('customer.detail.text.email'), value: c.email },
        {
          text: this.$t('customer.detail.text.receiveNotification'),
          value: c.receivesInfo
            ? this.$t('customer.detail.text.receive')
            : this.$t('customer.detail.text.notReceive'),
        },
        {
          text: this.$t('customer.detail.text.note'),
          value: c.note,
        },
        {
          text: this.$t('customer.detail.text.operationNote'),
          value: c.operationNote,
        },
        {
          text: this.$t('customer.detail.text.starterPassPurchaseDate'),
          value: c.starterPassFrom,
        },
        {
          text: this.$t('customer.detail.text.starterPassExpiryDate'),
          value: c.starterPassTo,
        },
      ]
    },
    patchTestText() {
      let c = this.customer
      if (!c) return null
      switch (c.patchTest) {
        case null:
          return this.$t('customer.detail.text.notRegistered')
        case true:
          return this.$t('customer.detail.text.implement')
        case false:
          return this.$t('customer.detail.text.notImplement')
      }
      return null
    },
    counselingAnswers() {
      let c = this.customer
      if (!c) return null
      return [
        {
          text: this.$t('customer.detail.text.patchTestOld'),
          value: this.patchTestText,
        },
        ...this.questions.map(this.getAnswer).filter(x => Boolean(x.value)),
      ]
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.customer = await this.$api.customers(this.id).get()
      this.questions = await this.$apiV3
        .counselingQuestions()
        .list({ all: true })
        .then(({ results }) => results)

      this.$nextTick(() => {
        this.fixHeight()
      })
    },
    getAnswer(question) {
      let { id, shortText, options } = question
      let answers = this.customer.counselingAnswers.filter(
        x => x.question === id
      )
      return {
        text: shortText,
        value: answers
          .map(({ option, text }) => {
            let opt = options.find(x => x.id === option)
            if (!opt) return null
            if (text) return `${opt.text}（${text}）`
            return opt.text
          })
          .filter(x => x)
          .join('、 '),
      }
    },

    fixHeight() {
      const rect = this.$refs.tableContainer.getBoundingClientRect()
      if (rect) {
        const footRect = this.$refs.bottom.getBoundingClientRect()

        let h = window.innerHeight - rect.top - footRect.height - 20
        if (h > 300) this.tableHeight = h + 'px'
      }
    },
  },
}
</script>

<style></style>
